import { useEffect, useState } from "react";
import type { Opportunity, Project, Person } from "../services/api";
import { createOpportunity, listOpportunities, updateOpportunity, deleteOpportunity, listProjects, listPeople } from "../services/api";
import { getErrorAlertMessage } from "../utils/errors";
import { Modal } from "./Modal";
import { useAuth } from "../auth/AuthContext";

export function OpportunitiesList() {
  const { hasRole } = useAuth();
  const canEdit = hasRole("admin") || hasRole("root");

  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedOpportunity, setSelectedOpportunity] = useState<Opportunity | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  useEffect(() => {
    void refresh();
  }, []);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const [opportunitiesData, projectsData, peopleData] = await Promise.all([
        listOpportunities(),
        listProjects(),
        listPeople(),
      ]);
      setOpportunities(opportunitiesData);
      setProjects(projectsData);
      setPeople(peopleData);
    } catch (err) {
      setError(getErrorAlertMessage("load opportunities", err));
    } finally {
      setLoading(false);
    }
  }

  function handleOpenOpportunity(opportunity: Opportunity) {
    setSelectedOpportunity(opportunity);
    setShowViewModal(true);
  }

  function handleCloseModals() {
    setSelectedOpportunity(null);
    setShowCreateModal(false);
    setShowViewModal(false);
    setModalError(null);
  }

  async function handleDelete(opportunityId: string) {
    if (!confirm("Are you sure you want to delete this opportunity?")) return;

    try {
      await deleteOpportunity(opportunityId);
      await refresh();
      handleCloseModals();
    } catch (err) {
      setModalError(getErrorAlertMessage("delete opportunity", err));
    }
  }

  return (
    <div className="layout single-column">
      <div className="panel">
        <div className="panel-header">
          <h2>Opportunities</h2>
          <div style={{ display: "flex", gap: "0.75rem" }}>
            {canEdit && (
              <button type="button" className="primary" onClick={() => setShowCreateModal(true)}>
                Create Opportunity
              </button>
            )}
            <button type="button" className="ghost" onClick={() => void refresh()} disabled={loading}>
              {loading ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        </div>
        {error && <div className="alert error">{error}</div>}
        {loading ? (
          <div className="panel">Loading opportunities…</div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Opportunity ID</th>
                  <th>Name</th>
                  <th>Customer</th>
                  <th>Due Date</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {opportunities.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="empty">
                      No opportunities found.
                    </td>
                  </tr>
                ) : (
                  opportunities.map((opportunity) => (
                    <tr key={opportunity.opportunity_id} onClick={() => handleOpenOpportunity(opportunity)} className="clickable-row">
                      <td>{opportunity.opportunity_id}</td>
                      <td>{opportunity.name}</td>
                      <td>{opportunity.customer_legal_name ?? "-"}</td>
                      <td>{opportunity.due_date ?? "-"}</td>
                      <td>{opportunity.created_at ? new Date(opportunity.created_at).toLocaleDateString() : "-"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Opportunity Modal */}
      <Modal
        open={showCreateModal}
        onClose={handleCloseModals}
        title="Create New Opportunity"
      >
        <OpportunityForm
          onSuccess={(newOpportunity) => {
            setOpportunities((prev) => [...prev, newOpportunity]);
            handleCloseModals();
          }}
          onError={(err) => setModalError(err)}
          saving={saving}
          setSaving={setSaving}
          error={modalError}
        />
      </Modal>

      {/* View Opportunity Modal */}
      <Modal
        open={showViewModal}
        onClose={handleCloseModals}
        title={selectedOpportunity ? `${selectedOpportunity.opportunity_id} - ${selectedOpportunity.name}` : undefined}
      >
        {selectedOpportunity && (
          <OpportunityDetailsView
            opportunity={selectedOpportunity}
            projects={projects}
            people={people}
            canEdit={canEdit}
            onUpdate={(updated) => {
              setOpportunities((prev) => prev.map((opp) => (opp.opportunity_id === updated.opportunity_id ? updated : opp)));
              setSelectedOpportunity(updated);
            }}
            onDelete={() => void handleDelete(selectedOpportunity.opportunity_id)}
          />
        )}
      </Modal>
    </div>
  );
}

function OpportunityDetailsView({
  opportunity,
  projects,
  people,
  canEdit,
  onUpdate,
  onDelete,
}: {
  opportunity: Opportunity;
  projects: Project[];
  people: Person[];
  canEdit: boolean;
  onUpdate: (opportunity: Opportunity) => void;
  onDelete: () => void;
}) {
  const [activeTab, setActiveTab] = useState<"overview" | "team">("overview");
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter projects linked to this opportunity
  const linkedProjects = projects.filter((project) => project.opportunity_id === opportunity.opportunity_id);

  return (
    <>
      {/* Tabs Navigation - hidden until Shape Team is ready
      <nav className="tabs" style={{ marginBottom: "1.5rem" }}>
        <button
          type="button"
          className={`tab-button ${activeTab === "overview" ? "active" : ""}`}
          onClick={() => setActiveTab("overview")}
        >
          Overview & RRs
        </button>
        <button
          type="button"
          className={`tab-button ${activeTab === "team" ? "active" : ""}`}
          onClick={() => setActiveTab("team")}
        >
          Shape Team
        </button>
      </nav>
      */}

      {/* Tab Content */}
      {activeTab === "overview" && (
        <>
          {canEdit && (
            <div style={{ marginBottom: "1.5rem", display: "flex", gap: "0.75rem" }}>
              <button type="button" className="ghost" onClick={() => setIsEditing(!isEditing)}>
                {isEditing ? "Cancel Edit" : "Edit Opportunity"}
              </button>
              {!isEditing && (
                <button type="button" className="danger" onClick={onDelete}>
                  Delete
                </button>
              )}
            </div>
          )}

          {/* Opportunity Summary */}
          <div
            style={{
              display: "flex",
              gap: "2rem",
              marginBottom: "1.5rem",
              flexWrap: "wrap",
              padding: "1rem",
              backgroundColor: "#f8fafc",
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
              <span className="detail-label">Customer</span>
              <span className="detail-value" style={{ fontWeight: "600" }}>
                {opportunity.customer_legal_name || "-"}
              </span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
              <span className="detail-label">Due Date</span>
              <span className="detail-value" style={{ fontWeight: "600" }}>
                {opportunity.due_date || "-"}
              </span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
              <span className="detail-label">Created</span>
              <span className="detail-value" style={{ fontWeight: "600" }}>
                {opportunity.created_at ? new Date(opportunity.created_at).toLocaleDateString() : "-"}
              </span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
              <span className="detail-label">Total RRs</span>
              <span className="detail-value" style={{ fontWeight: "600", color: "#0b7285" }}>
                {linkedProjects.length}
              </span>
            </div>
          </div>

          {isEditing && canEdit ? (
            <OpportunityForm
              opportunity={opportunity}
              onSuccess={(updated) => {
                onUpdate(updated);
                setIsEditing(false);
                setError(null);
              }}
              onError={(err) => setError(err)}
              saving={saving}
              setSaving={setSaving}
              error={error}
            />
          ) : (
            <>
              {/* Linked RRs Table */}
              <div style={{ marginTop: "1.5rem" }}>
                <h3 style={{ marginBottom: "1rem" }}>Linked RRs ({linkedProjects.length})</h3>
                {linkedProjects.length === 0 ? (
                  <div className="alert info">No RRs linked to this opportunity yet.</div>
                ) : (
                  <div className="table-wrapper">
                    <table>
                      <thead>
                        <tr>
                          <th>RR ID</th>
                          <th>Title</th>
                          <th>Status</th>
                          <th>Start Date</th>
                          <th>End Date</th>
                          <th>Role</th>
                        </tr>
                      </thead>
                      <tbody>
                        {linkedProjects.map((project) => (
                          <tr key={project.project_id}>
                            <td>{project.project_id}</td>
                            <td>{project.title}</td>
                            <td>{project.status || "-"}</td>
                            <td>{project.start_date || "-"}</td>
                            <td>{project.end_date || "-"}</td>
                            <td>{project.aws_role || project.tw_role || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}

      {activeTab === "team" && (
        <div>
          <h3 style={{ marginBottom: "1rem" }}>Shape Team</h3>
          <p style={{ marginBottom: "1.5rem", color: "#64748b" }}>
            View available consultants and build your team for this opportunity.
          </p>

          {/* People List */}
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Region</th>
                  <th>Speciality</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {people.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="empty">
                      No consultants available.
                    </td>
                  </tr>
                ) : (
                  people.map((person) => (
                    <tr key={person.email}>
                      <td>{person.name}</td>
                      <td>{person.email}</td>
                      <td>{person.region || "-"}</td>
                      <td>{person.speciality || "-"}</td>
                      <td>{person.status || "-"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

function OpportunityForm({
  opportunity,
  onSuccess,
  onError,
  saving,
  setSaving,
  error,
}: {
  opportunity?: Opportunity;
  onSuccess: (opportunity: Opportunity) => void;
  onError: (error: string) => void;
  saving: boolean;
  setSaving: (value: boolean) => void;
  error: string | null;
}) {
  const [formData, setFormData] = useState({
    name: opportunity?.name ?? "",
    customer_legal_name: opportunity?.customer_legal_name ?? "",
    due_date: opportunity?.due_date ?? "",
  });

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    onError("");

    try {
      const payload: Partial<Opportunity> = {
        name: formData.name,
        customer_legal_name: formData.customer_legal_name || undefined,
        due_date: formData.due_date || undefined,
      };

      let result: Opportunity;
      if (opportunity) {
        result = await updateOpportunity(opportunity.opportunity_id, payload);
      } else {
        result = await createOpportunity(payload);
      }

      onSuccess(result);
    } catch (err) {
      onError(getErrorAlertMessage(opportunity ? "update opportunity" : "create opportunity", err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="form-grid" onSubmit={handleSubmit}>
      <label className="span-2">
        Opportunity Name <span style={{ color: "#b91c1c" }}>*</span>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => handleChange("name", e.target.value)}
          required
          placeholder="e.g. AWS Migration Project"
        />
      </label>

      <label className="span-2">
        Customer Legal Name
        <input
          type="text"
          value={formData.customer_legal_name}
          onChange={(e) => handleChange("customer_legal_name", e.target.value)}
          placeholder="e.g. Acme Corporation Inc."
        />
      </label>

      <label>
        Due Date
        <input
          type="date"
          value={formData.due_date}
          onChange={(e) => handleChange("due_date", e.target.value)}
        />
      </label>

      {error && (
        <div className="span-2 alert error">{error}</div>
      )}

      <div className="form-actions span-2">
        <button type="submit" className="primary" disabled={saving}>
          {saving ? (opportunity ? "Updating…" : "Creating…") : (opportunity ? "Update Opportunity" : "Create Opportunity")}
        </button>
      </div>
    </form>
  );
}
